import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// @ts-ignore-next-line importsNotUsedAsValues
import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/fp/isEqual';
import isEmpty from 'lodash/fp/isEmpty';
import mapValues from 'lodash/fp/mapValues';
import some from 'lodash/fp/some';
import noop from 'lodash/fp/noop';
import { arrayMove } from '@dnd-kit/sortable';
import Dialog from '../dialog/Dialog';
import ClearableInput from '../clearableInput/ClearableInput';
import { TableSettingsDialogFooter } from './TableSettingsDialogFooter';
import { TableSettingsListContainer } from './TableSettingsListContainer';
import { filterColumns } from './TableSettingsListItem';
const DEFAULT_COLUMN_WIDTH = 0;
const MAX_COLUMN_WIDTH = 1000;
const TableSettingsDialog = (props) => {
    const { show = false, title, subtitle, className, defaultColumnOrder = [], defaultHiddenColumns = [], columnOrder: extColumnOrder = [], hiddenColumns: extHiddenColumns = [], columnLabels = {}, disabledColumns = [], columnsDetails: extColumnsDetails = {}, autoLabel = '', applyButtonText, cancelButtonText, closeButtonText, resetButtonText, onColumnChange = noop, onColumnDetailsChange = noop, onDiscard = noop, 
    // onCancel = noop,
    onApply = noop, onHide, columnSearchValue: extColumnSearchValue = '', onSearchChange = noop, searchPlaceholder, notFoundMessage = '', immediateChange = false, } = props;
    const [columnSearchValue, setColumnSearchValue] = useState(extColumnSearchValue);
    const [columnOrder, setColumnOrder] = useState(extColumnOrder);
    const [hiddenColumns, setHiddenColumns] = useState(extHiddenColumns || defaultHiddenColumns);
    const [columnsDetails, setColumnsDetails] = useState(extColumnsDetails);
    const [openColumnsDetails, setOpenColumnsDetails] = useState({});
    const [columnLabelStrings, setColumnLabelStrings] = useState({});
    const [updateColumnLabelStrings, setUpdateColumnLabelStrings] = useState(true);
    // Dirty flag for offering to reset changes or to discard them
    const [hasChanged, setHasChanged] = useState(false);
    const [isResetAll, setIsResetAll] = useState(false);
    const [movedColumn, setMovedColumn] = useState(false);
    const contentRef = useRef(null);
    // Update items from outside
    useEffect(() => {
        setColumnSearchValue(extColumnSearchValue);
        setColumnOrder(extColumnOrder);
        setHiddenColumns(extHiddenColumns);
        if (show) {
            getColumnLabelStringsFromDOM();
        }
    }, [extColumnSearchValue, extColumnOrder, extHiddenColumns, show]);
    const hasColumnsDetailsChanged = (columnsDetailsToCheck) => {
        if (isEmpty(columnsDetailsToCheck)) {
            return false;
        }
        const hasObjectChanged = some((details) => {
            const defaultWidth = Number.isFinite(details.defaultWidth) ? details.defaultWidth : DEFAULT_COLUMN_WIDTH;
            return details.width !== defaultWidth;
        })(columnsDetailsToCheck);
        return hasObjectChanged;
    };
    // Update column details from outside if provided. Note, that in "sort columns only" mode
    // the columns details are undefined and no column widths can be set
    const [previousColumnDetails, setPreviousColumnDetails] = useState(extColumnsDetails);
    if (!isEqual(columnsDetails, previousColumnDetails)) {
        const columnsDetailsChanged = hasColumnsDetailsChanged(extColumnsDetails);
        setColumnsDetails(columnsDetailsChanged ? extColumnsDetails : columnsDetails);
        setPreviousColumnDetails(extColumnsDetails);
    }
    const getColumnLabelStringsFromDOM = () => {
        if (!contentRef.current) {
            return;
        }
        // For searching by name we need to get the label from the DOM as it may contain a FormattedMessage
        const labels = contentRef.current.getElementsByClassName('table-settings-item-label');
        const columnStrings = {};
        [...labels].map(label => {
            var _a;
            const dataKey = label.getAttribute('data-key');
            if (dataKey) {
                const updatedLabel = (_a = label.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\r?\n|\r/g, '').toLowerCase();
                if (updatedLabel) {
                    columnStrings[dataKey] = updatedLabel;
                }
            }
        });
        setColumnLabelStrings(columnStrings);
        setUpdateColumnLabelStrings(false);
    };
    const deleteMovedColumn = () => {
        setMovedColumn(false);
    };
    const moveColumnToIndex = (columnName, newIndex, changeMovedColumn) => {
        const newColumnOrder = columnOrder.filter(name => name !== columnName);
        newColumnOrder.splice(newIndex, 0, columnName);
        setColumnOrder(newColumnOrder);
        setMovedColumn(changeMovedColumn ? !!columnName : false);
        setHasChanged(true);
        if (immediateChange) {
            onColumnChange(newColumnOrder, hiddenColumns);
        }
        window.setTimeout(deleteMovedColumn, 500);
    };
    const handleResetColumnChanges = () => setIsResetAll(true);
    const handleCancelResetColumnChanges = () => setIsResetAll(false);
    const resetColumnsDetails = (details) => {
        return mapValues((singleColumnDetails) => {
            return Object.assign(Object.assign({}, singleColumnDetails), { width: singleColumnDetails.defaultWidth || DEFAULT_COLUMN_WIDTH });
        })(details);
    };
    const resetAllColumnChanges = () => {
        const defaultColumnsDetails = resetColumnsDetails(columnsDetails);
        setColumnOrder(defaultColumnOrder);
        setHiddenColumns(defaultHiddenColumns);
        setColumnSearchValue('');
        setHasChanged(false);
        setIsResetAll(false);
        if (!isEmpty(columnsDetails)) {
            setColumnsDetails(defaultColumnsDetails);
        }
        if (immediateChange) {
            onSearchChange('');
            onColumnChange(defaultColumnOrder, defaultHiddenColumns, defaultColumnsDetails);
        }
    };
    const discardColumnChanges = () => {
        onSearchChange('');
        // onCancel();
        onDiscard();
        onHide();
    };
    const handleManuallyApplyChanges = () => {
        setColumnSearchValue('');
        onSearchChange('');
        onColumnChange(columnOrder, hiddenColumns, columnsDetails);
        onApply(columnOrder, hiddenColumns, columnsDetails);
        onHide();
    };
    const toggleHideColumn = (column) => {
        const isHidden = hiddenColumns.includes(column);
        const newHiddenColumns = isHidden ? hiddenColumns.filter(name => name !== column) : [...hiddenColumns, column];
        setHiddenColumns(newHiddenColumns);
        setHasChanged(true);
        if (immediateChange) {
            onColumnChange(columnOrder, newHiddenColumns);
        }
    };
    const handleSearchChange = (searchValue) => {
        const newSearch = searchValue.toLowerCase();
        setColumnSearchValue(() => {
            onSearchChange(newSearch);
            return newSearch;
        });
    };
    const handleColumnWidthChange = (column, value) => {
        if (columnsDetails[column]) {
            columnsDetails[column].width = value;
        }
        else {
            columnsDetails[column] = {
                width: value,
                defaultWidth: 0,
                maxWidth: MAX_COLUMN_WIDTH,
            };
        }
        setColumnsDetails(columnsDetails);
        setHasChanged(true);
        if (immediateChange) {
            onColumnDetailsChange(column, columnsDetails[column]);
        }
    };
    const handleResetColumnWidth = (column) => {
        const updatedColumnDetails = columnsDetails[column];
        updatedColumnDetails.width = updatedColumnDetails.defaultWidth;
        setColumnsDetails(columnsDetails);
        if (immediateChange) {
            onColumnDetailsChange(column, columnsDetails[column]);
        }
    };
    const handleOpenColumnsDetails = (columnName) => {
        const updatedOpenColumnDetails = Object.assign({}, openColumnsDetails);
        if (updatedOpenColumnDetails[columnName]) {
            delete updatedOpenColumnDetails[columnName];
        }
        else {
            updatedOpenColumnDetails[columnName] = columnName;
        }
        setOpenColumnsDetails(updatedOpenColumnDetails);
    };
    const handleSortEnd = (event, previousOrder) => {
        const { active, over } = event;
        const activeId = active.id;
        const overId = over === null || over === void 0 ? void 0 : over.id;
        if (activeId === overId) {
            return;
        }
        const oldIndex = previousOrder.indexOf(String(activeId));
        const newIndex = previousOrder.indexOf(String(overId));
        const newColumnOrder = arrayMove(previousOrder, oldIndex, newIndex);
        setColumnOrder(newColumnOrder);
        setMovedColumn(true);
        setHasChanged(true);
        if (immediateChange) {
            onColumnChange(newColumnOrder, hiddenColumns);
        }
    };
    const renderTableSettingsDialogContent = () => {
        const itemProps = {
            columnLabels,
            autoLabel,
            disabledColumns,
            columnOrder,
            hiddenColumns,
            columnSearchValue,
            columnsDetails,
            columnLabelStrings,
            openColumnsDetails,
            updateColumnLabelStrings,
            onMoveColumn: moveColumnToIndex,
            onOpenDetails: handleOpenColumnsDetails,
            onColumnWidthChange: handleColumnWidthChange,
            onResetColumnWidth: handleResetColumnWidth,
            onToggleHideColumn: toggleHideColumn,
        };
        const filteredColumns = columnOrder.filter(column => filterColumns(columnSearchValue, column, columnLabelStrings));
        const hasItems = !isEqual(filteredColumns, columnOrder);
        return (_jsxs("div", Object.assign({ ref: contentRef }, { children: [_jsx("div", Object.assign({ className: 'table-settings-search' }, { children: _jsxs("div", Object.assign({ className: 'input-group width-100pct' }, { children: [_jsx("span", Object.assign({ className: 'input-group-addon' }, { children: _jsx("span", { className: 'rioglyph rioglyph-search' }) })), _jsx(ClearableInput, { value: columnSearchValue, onChange: handleSearchChange, placeholder: searchPlaceholder })] })) })), _jsx("div", Object.assign({ className: 'table-settings-body' }, { children: hasItems ? (_jsx(TableSettingsListContainer, { items: columnOrder, onSortEnd: handleSortEnd, itemProps: Object.assign({}, itemProps) })) : (_jsx("div", Object.assign({ className: 'text-center text-color-gray' }, { children: notFoundMessage }))) }))] })));
    };
    const renderTableSettingsDialogFooter = () => {
        return (_jsx(TableSettingsDialogFooter, { hasChanged: hasChanged, isResetAll: isResetAll, immediateChange: immediateChange, resetButtonText: resetButtonText, closeButtonText: closeButtonText, cancelButtonText: cancelButtonText, applyButtonText: applyButtonText, onHide: onHide, onResetColumnChanges: handleResetColumnChanges, onDiscardChanges: discardColumnChanges, onApplyChanges: handleManuallyApplyChanges, onConfirmResetColumnChanges: resetAllColumnChanges, onCancelResetColumnChanges: handleCancelResetColumnChanges }));
    };
    if (!show) {
        return null;
    }
    const dialogClassNames = classNames('TableSettingsDialog', className);
    return (_jsx(Dialog, { show: show, title: title, subtitle: subtitle, onClose: onHide, body: renderTableSettingsDialogContent(), footer: renderTableSettingsDialogFooter(), className: dialogClassNames }));
};
export default TableSettingsDialog;
